/**
 * Menu Visibility Configuration (LEGACY - Mostly Deprecated)
 * 
 * ⚠️ IMPORTANT: Environment-based menu filtering is DEPRECATED.
 * Menu access is now controlled per-employee via:
 *   AdminPanel > Employees > [Employee] > Menu Access
 * 
 * This file remains for backwards compatibility and potential env-based overrides,
 * but both dev and prod now use mode: 'all' by default.
 * 
 * The actual menu filtering happens in ProjectSidebar.js via:
 * - /auth/my-menu-permissions API (employee-specific menu access)
 * - Admins bypass all menu restrictions
 * 
 * Environment Variables (optional overrides):
 * - REACT_APP_MENU_MODE: 'all' | 'whitelist' | 'blacklist'
 * - REACT_APP_MENU_ENABLED: Comma-separated list of menu item IDs (for whitelist mode)
 * - REACT_APP_MENU_DISABLED: Comma-separated list of menu item IDs (for blacklist mode)
 */

// Default visibility configuration per environment
// 
// NOTE: Environment-based whitelist is DEPRECATED.
// Menu access is now controlled per-employee via AdminPanel > Employees > Menu Access.
// Both dev and prod use 'all' mode - the employee menu permissions system handles filtering.
//
const defaultConfig = {
  development: {
    mode: 'all',
    enabled: [],
    disabled: []
  },
  production: {
    mode: 'all', // Menu access controlled by employee permissions, not environment whitelist
    enabled: [],
    disabled: []
  }
};

/**
 * Get the current menu visibility configuration
 * Priority: Environment variables > Default config
 */
export const getMenuConfig = () => {
  const env = process.env.NODE_ENV || 'development';
  const envConfig = defaultConfig[env] || defaultConfig.development;
  
  // Check for environment variable overrides
  const menuMode = process.env.REACT_APP_MENU_MODE || envConfig.mode;
  
  let enabled = envConfig.enabled;
  let disabled = envConfig.disabled;
  
  // Parse environment variables if provided
  if (process.env.REACT_APP_MENU_ENABLED) {
    enabled = process.env.REACT_APP_MENU_ENABLED.split(',').map(s => s.trim()).filter(Boolean);
  }
  
  if (process.env.REACT_APP_MENU_DISABLED) {
    disabled = process.env.REACT_APP_MENU_DISABLED.split(',').map(s => s.trim()).filter(Boolean);
  }
  
  return {
    mode: menuMode,
    enabled: new Set(enabled),
    disabled: new Set(disabled)
  };
};

/**
 * Check if a menu item should be visible
 * @param {string} itemId - The menu item ID
 * @param {string} parentId - The parent menu item ID (optional)
 * @param {Object} config - The menu configuration (optional, will be fetched if not provided)
 * @returns {boolean} - Whether the item should be visible
 */
export const isMenuItemVisible = (itemId, parentId = null, config = null) => {
  const menuConfig = config || getMenuConfig();
  
  switch (menuConfig.mode) {
    case 'all':
      return true;
      
    case 'whitelist':
      // In whitelist mode, item must be explicitly in the enabled list
      // This ensures granular control - child items are NOT auto-enabled when parent is enabled
      return menuConfig.enabled.has(itemId);
      
    case 'blacklist':
      // Item is visible unless it's in the disabled list
      return !menuConfig.disabled.has(itemId);
      
    default:
      return true;
  }
};

/**
 * Filter menu structure based on visibility configuration
 * @param {Array} menuStructure - The full menu structure
 * @returns {Array} - Filtered menu structure with only visible items
 */
export const filterMenuStructure = (menuStructure) => {
  const config = getMenuConfig();
  
  if (config.mode === 'all') {
    return menuStructure;
  }
  
  const filterItems = (items, parentId = null) => {
    return items
      .filter(item => isMenuItemVisible(item.id, parentId, config))
      .map(item => {
        if (item.subItems && item.subItems.length > 0) {
          const filteredSubItems = filterItems(item.subItems, item.id);
          // Only include the item if it has visible sub-items or is itself visible
          if (filteredSubItems.length > 0) {
            return { ...item, subItems: filteredSubItems };
          }
          // If no sub-items are visible, check if the parent itself should still be shown
          // (for items that can be clicked directly)
          return null;
        }
        return item;
      })
      .filter(Boolean);
  };
  
  return filterItems(menuStructure);
};

/**
 * Get a human-readable list of all menu item IDs
 * Useful for documentation and configuration
 * @param {Array} menuStructure - The full menu structure
 * @returns {Array} - Array of { id, label, level, parent } objects
 */
export const getAllMenuItemIds = (menuStructure) => {
  const items = [];
  
  const collectItems = (structure, level = 0, parent = null) => {
    structure.forEach(item => {
      items.push({
        id: item.id,
        label: item.label,
        level,
        parent
      });
      
      if (item.subItems) {
        collectItems(item.subItems, level + 1, item.id);
      }
    });
  };
  
  collectItems(menuStructure);
  return items;
};

// Export default config for reference
export { defaultConfig };

